// frontend/src/components/mcbim/RoomColoringMenu.tsx
import React, { useMemo, useState } from "react";
import {
  Badge,
  Box,
  Button,
  Divider,
  Group,
  Menu,
  NavLink,
  ScrollArea,
  Stack,
  Text,
  Tooltip,
} from "@mantine/core";
import { MdPalette } from "react-icons/md";
import { FiCheck, FiChevronDown } from "react-icons/fi";

import { useAppDispatch, useAppSelector } from "../app/hooks";
import { selectRoomColorings } from "../Room/roomSelectors";
import { ROOM_COLORING_IDS, ERoomColoringCategoryId, IRoomColoring } from ".";
import { ROOM_COLORING_CATEGORIES } from "./roomColoringConstants";
import { setActiveRoomColoringIdThunk } from "../DesignTab/designThunk";
import { MEDIA_ASSET_KEYS } from "@multiconsult-group/multiplacer-core";
import { MediaAssetViewer } from "../MediaAssets/MediaAssetViewer";

type RoomColoringMenuTone = "default" | "primary";

type RoomColoringMenuProps = {
  tone?: RoomColoringMenuTone;
};

const groupByCategory = (items: IRoomColoring[]): Record<string, IRoomColoring[]> => {
  return items.reduce(
    (acc, item) => {
      const k = item.categoryId ?? ERoomColoringCategoryId.Annet;
      (acc[k] ??= []).push(item);
      return acc;
    },
    {} as Record<string, IRoomColoring[]>
  );
};

const sortRoomColorings = (a: IRoomColoring, b: IRoomColoring) => {
  const ao = a.order ?? 9999;
  const bo = b.order ?? 9999;
  if (ao !== bo) return ao - bo;
  return a.title.localeCompare(b.title);
};

const getCategoryTitle = (categoryId: string): string => {
  const hit = ROOM_COLORING_CATEGORIES.find((c) => c.id === categoryId);
  return hit?.titleNo ?? categoryId;
};

export const RoomColoringMenu: React.FC<RoomColoringMenuProps> = ({ tone = "default" }) => {
  const dispatch = useAppDispatch();

  const roomColorings = useAppSelector(selectRoomColorings);
  const activeRoomColoringId = useAppSelector((state) => state.design.activeRoomColoringId);

  const currentFilter = useMemo(
    () => roomColorings.find((f) => f.id === activeRoomColoringId),
    [roomColorings, activeRoomColoringId]
  );

  const hasActive = !!currentFilter;

  const tooltipLabel = hasActive ? "Endre romfargelegging" : "Velg romfargelegging";

  const handleSelect = (id: string | null) => {
    dispatch(
      setActiveRoomColoringIdThunk(
        id ? (id as ROOM_COLORING_IDS) : (undefined as unknown as ROOM_COLORING_IDS)
      )
    );
  };

  const grouped = useMemo(() => groupByCategory(roomColorings), [roomColorings]);

  const orderedCategoryIds = useMemo(() => {
    const known = [...ROOM_COLORING_CATEGORIES].sort((a, b) => a.order - b.order).map((c) => c.id);
    const unknown = Object.keys(grouped).filter(
      (id) => !known.includes(id as ERoomColoringCategoryId)
    );
    return [...known, ...unknown];
  }, [grouped]);

  const activeCategory = useMemo(() => {
    if (!activeRoomColoringId) return "none";
    return currentFilter?.categoryId ?? ERoomColoringCategoryId.Annet;
  }, [activeRoomColoringId, currentFilter]);

  const [displayedCategory, setDisplayedCategory] = useState(activeCategory);

  const activeGroup = displayedCategory !== "none" ? (grouped[displayedCategory] ?? []) : [];
  const isPrimary = tone === "primary";

  return (
    <Group gap="xs" align="center" wrap="nowrap">
      <MediaAssetViewer assetKey={MEDIA_ASSET_KEYS.COLORFILTER} mode="ICON" />
      <Menu
        withinPortal
        position="bottom-start"
        shadow="xl"
        width={640}
        closeOnItemClick={false}
        styles={{ dropdown: { backgroundColor: "rgba(255, 255, 255, 0.95)" } }}
      >
        <Menu.Target>
          <Tooltip label={tooltipLabel} withArrow position="bottom">
            <Button
              size="sm"
              variant={isPrimary ? "filled" : "subtle"}
              color={isPrimary ? "blue" : undefined}
              leftSection={<MdPalette size={16} />}
              rightSection={<FiChevronDown size={14} />}
              w={280}
              justify="space-between"
              styles={{
                label: { width: "100%" },
                root: isPrimary
                  ? {
                      boxShadow: "0 4px 12px rgba(25, 113, 194, 0.28)",
                    }
                  : undefined,
              }}
            >
              <Text truncate fw={500}>
                {hasActive ? currentFilter.title : "Velg romfargelegging"}
              </Text>
            </Button>
          </Tooltip>
        </Menu.Target>

        <Menu.Dropdown>
          {/* Column headers */}
          <Box style={{ display: "flex" }} p="md" pb={8}>
            <Box style={{ flex: "0 0 240px", paddingLeft: 12 }}>
              <Text fw={700} size="sm">
                Kategorier
              </Text>
            </Box>
            <Box style={{ flex: 1, paddingLeft: 12 }}>
              <Text fw={700} size="sm">
                {displayedCategory !== "none"
                  ? `Filter i ${getCategoryTitle(displayedCategory)}`
                  : "Ingen romfargelegging"}
              </Text>
            </Box>
          </Box>

          <Divider />

          {/* Two-column layout */}
          <Box style={{ display: "flex", height: 400 }}>
            {/* Left: Categories */}
            <Box style={{ flex: "0 0 240px" }}>
              <ScrollArea h="100%" type="hover">
                <Stack gap={0}>
                  <NavLink
                    active={displayedCategory === "none"}
                    label={
                      <Text truncate fw={displayedCategory === "none" ? 600 : 400}>
                        Ingen romfargelegging
                      </Text>
                    }
                    onClick={() => {
                      setDisplayedCategory("none");
                      handleSelect(null);
                    }}
                    variant="subtle"
                    styles={{
                      root: {
                        borderRadius: 0,
                        padding: "10px 12px",
                      },
                      label: {
                        width: "100%",
                      },
                    }}
                  />
                  <Divider my="md" />
                  {orderedCategoryIds.map((id) => {
                    const title = getCategoryTitle(id);
                    const items = grouped[id] ?? [];
                    const isActive = displayedCategory === id;

                    return (
                      <NavLink
                        key={id}
                        active={isActive}
                        label={
                          <Group justify="space-between" wrap="nowrap" gap={8} w="100%">
                            <Text truncate fw={isActive ? 600 : 400}>
                              {title}
                            </Text>
                            <Badge variant="light" color="blue" size="xs">
                              {items.length}
                            </Badge>
                          </Group>
                        }
                        onClick={() => setDisplayedCategory(id)}
                        variant="subtle"
                        styles={{
                          root: {
                            borderRadius: 0,
                            padding: "10px 12px",
                          },
                          label: {
                            width: "100%",
                          },
                        }}
                      />
                    );
                  })}
                </Stack>
              </ScrollArea>
            </Box>

            {/* Right: Filters */}
            <Box style={{ flex: 1 }}>
              <ScrollArea h="100%" type="hover">
                <Stack gap={0} p="md">
                  {displayedCategory === "none" ? (
                    <Text size="sm" c="dimmed" ta="center">
                      Ingen romfargelegging valgt
                    </Text>
                  ) : activeGroup.length === 0 ? (
                    <Text size="sm" c="dimmed" ta="center">
                      Ingen filter i denne kategorien
                    </Text>
                  ) : (
                    activeGroup
                      .slice()
                      .sort(sortRoomColorings)
                      .map((filter) => {
                        const isSelected = filter.id === activeRoomColoringId;
                        const short = filter.explanation?.shortNo;
                        const long = filter.explanation?.longNo;
                        const tooltip = long ?? short;

                        return (
                          <Menu.Item
                            key={filter.id}
                            onClick={() => {
                              handleSelect(filter.id);
                              setDisplayedCategory(
                                filter.categoryId ?? ERoomColoringCategoryId.Annet
                              );
                            }}
                            leftSection={
                              isSelected ? (
                                <FiCheck size={16} color="var(--mantine-color-blue-6)" />
                              ) : null
                            }
                            rightSection={
                              isSelected ? (
                                <Badge variant="light" color="blue" size="xs">
                                  Aktiv
                                </Badge>
                              ) : null
                            }
                            styles={{
                              item: {
                                padding: "10px 12px",
                                borderRadius: 4,
                              },
                            }}
                          >
                            <Tooltip
                              label={tooltip}
                              withArrow
                              position="left"
                              disabled={!tooltip}
                              multiline
                              w={340}
                            >
                              <Text size="sm" truncate>
                                {filter.title}
                              </Text>
                            </Tooltip>
                          </Menu.Item>
                        );
                      })
                  )}
                </Stack>
              </ScrollArea>
            </Box>
          </Box>
        </Menu.Dropdown>
      </Menu>
    </Group>
  );
};
